import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  placement: string;
  className?: string;
}

const TIER_WEIGHT: Record<string, number> = { anchor: 3, catalyst: 2, community: 1 };

export default function SponsorSlot({ placement, className }: Props) {
  const { data: sponsor } = useQuery({
    queryKey: ["sponsor-slot", placement],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("sponsors")
        .select("*")
        .eq("is_active", true)
        .contains("placements", [placement]);
      if (error) throw error;
      const active = (data as any[]).filter(
        (s) => !s.ends_at || new Date(s.ends_at) > new Date()
      );
      if (!active.length) return null;
      const weighted = active.flatMap((s) =>
        Array(TIER_WEIGHT[s.tier] || 1).fill(s)
      );
      return weighted[Math.floor(Math.random() * weighted.length)];
    },
    staleTime: 1000 * 60,
  });

  useEffect(() => {
    if (!sponsor) return;
    (supabase as any)
      .from("sponsor_impressions")
      .insert({
        sponsor_id: sponsor.id,
        placement,
        route: typeof window !== "undefined" ? window.location.pathname : null,
      })
      .then(() => {});
  }, [sponsor?.id, placement]);

  if (!sponsor) return null;

  return (
    <a
      href={sponsor.link_url || "#"}
      target="_blank"
      rel="sponsored noopener noreferrer"
      className={`block rounded-xl border border-border bg-card/50 hover:border-primary/40 transition-colors p-4 ${className ?? ""}`}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
        Sponsored · {sponsor.tier}
      </div>
      <div className="flex items-start gap-3">
        {sponsor.logo_url && (
          <img src={sponsor.logo_url} alt={sponsor.name} className="h-10 w-10 rounded object-cover" />
        )}
        <div className="min-w-0">
          <div className="font-semibold text-foreground text-sm">{sponsor.name}</div>
          {sponsor.blurb && (
            <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{sponsor.blurb}</div>
          )}
        </div>
      </div>
    </a>
  );
}